// AdminShell — dedicated chrome for the admin CPanel. Visually distinct from the
// main app shell: slate-900 sidebar with indigo accents to clearly signal
// "you're in admin mode". Includes a sticky impersonation banner when active.
//
// Phase 23 follow-up: sidebar is grouped into four logical sections
// (Insights / Data / Operations / Configuration) so admins can scan the
// 15 surface routes by intent rather than scrolling a flat list.

import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Activity,
  Users,
  Building2,
  LineChart,
  Cpu,
  Landmark,
  Lightbulb,
  ScrollText,
  Settings,
  LogOut,
  ExternalLink,
  Shield,
  Eye,
  EyeOff,
  Megaphone,
  Wrench,
  CreditCard,
  Flag,
  Home,
  ChartLine,
  Mail,
  Globe2,
  Filter,
  ListTree,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// 15 admin surfaces grouped into four sections. The order inside each group
// reflects expected daily-use frequency (top = most-used).
const NAV_GROUPS = [
  {
    label: "Insights",
    items: [
      { to: "/admin", end: true, label: "Home", Icon: Home, tid: "admin-nav-home" },
      { to: "/admin/ai-calls", label: "AI Usage", Icon: Cpu, tid: "admin-nav-aicalls" },
      { to: "/admin/viral-attribution", label: "Viral Attribution", Icon: ChartLine, tid: "admin-nav-viral" },
      { to: "/admin/health", label: "System Health", Icon: Activity, tid: "admin-nav-health" },
    ],
  },
  {
    label: "Data",
    items: [
      { to: "/admin/users", label: "Users", Icon: Users, tid: "admin-nav-users" },
      { to: "/admin/deals", label: "Deals", Icon: LineChart, tid: "admin-nav-deals" },
      { to: "/admin/properties", label: "Properties", Icon: Building2, tid: "admin-nav-properties" },
      { to: "/admin/leases", label: "Leases", Icon: ScrollText, tid: "admin-nav-leases" },
      { to: "/admin/tenants", label: "Tenants", Icon: Landmark, tid: "admin-nav-tenants" },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/admin/maintenance", label: "Maintenance", Icon: Wrench, tid: "admin-nav-maintenance" },
      { to: "/admin/payments", label: "Payments", Icon: CreditCard, tid: "admin-nav-payments" },
      { to: "/admin/email", label: "Email", Icon: Mail, tid: "admin-nav-email" },
      { to: "/admin/announcements", label: "Announcements", Icon: Megaphone, tid: "admin-nav-announcements" },
      { to: "/admin/audit-log", label: "Audit Log", Icon: ScrollText, tid: "admin-nav-audit" },
    ],
  },
  {
    label: "Analytics",
    items: [
      { to: "/admin/analytics/usage", label: "Usage", Icon: ChartLine, tid: "admin-nav-usage" },
      { to: "/admin/analytics/funnel", label: "Funnel", Icon: Filter, tid: "admin-nav-funnel" },
      { to: "/admin/analytics/geo", label: "Geo", Icon: Globe2, tid: "admin-nav-geo" },
      { to: "/admin/events", label: "Event Feed", Icon: ListTree, tid: "admin-nav-events" },
    ],
  },
  {
    label: "Configuration",
    items: [
      { to: "/admin/feature-flags", label: "Feature Flags", Icon: Flag, tid: "admin-nav-flags" },
      { to: "/admin/integrations", label: "Integrations", Icon: Settings, tid: "admin-nav-integrations" },
    ],
  },
];

// Flattened list for the mobile horizontal nav (no section labels — too wide).
const NAV_FLAT = NAV_GROUPS.flatMap((g) => g.items);

function ImpersonationBanner({ user, onStop }) {
  if (!user?.impersonated_by) return null;
  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-50 flex items-center justify-between gap-3 bg-red-600 px-4 py-2 pt-[calc(env(safe-area-inset-top)+0.5rem)] text-sm font-medium text-white shadow-md"
    >
      <span className="flex items-center gap-2">
        <Eye className="h-4 w-4" />
        Viewing as <span className="rounded bg-white/15 px-1.5 py-0.5">{user.email}</span>
      </span>
      <button
        type="button"
        onClick={onStop}
        data-testid="stop-impersonating"
        className="inline-flex items-center gap-1 rounded bg-white/15 px-2.5 py-1 text-xs font-semibold uppercase tracking-wider transition-colors hover:bg-white/25"
      >
        <EyeOff className="h-3 w-3" /> Stop impersonating
      </button>
    </div>
  );
}

function SidebarLink({ to, end, label, Icon, tid }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={tid}
      className={({ isActive }) =>
        `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150 ${
          isActive
            ? "bg-indigo-600 text-white shadow-sm"
            : "text-slate-300 hover:bg-slate-800 hover:text-white"
        }`
      }
    >
      <Icon className="h-4 w-4" />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

export default function AdminShell() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const onStopImpersonating = async () => {
    try {
      await api.post("/admin/stop-impersonating");
      await refresh();
      toast.success("Restored admin session");
      navigate("/admin");
    } catch (e) {
      toast.error("Could not stop impersonation");
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900">
      {/* Sidebar (desktop) */}
      <aside className="hidden w-64 flex-shrink-0 flex-col bg-slate-900 text-slate-200 md:flex" data-testid="admin-sidebar">
        <div className="flex h-16 items-center gap-2 border-b border-slate-800 px-5">
          <Shield className="h-5 w-5 text-indigo-400" />
          <Link to="/admin" className="text-base font-semibold tracking-tight" data-testid="admin-logo">
            runidea / Admin
          </Link>
        </div>
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-4" aria-label="Admin">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="mb-3" data-testid={`admin-nav-group-${group.label.toLowerCase()}`}>
              <div className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                {group.label}
              </div>
              <div className="flex flex-col gap-0.5">
                {group.items.map((item) => (
                  <SidebarLink key={item.to} {...item} />
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="border-t border-slate-800 p-3">
          <Link
            to="/dashboard"
            data-testid="admin-exit"
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-slate-400 transition-colors hover:bg-slate-800 hover:text-white"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Exit admin → user view
          </Link>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="flex w-full flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-slate-200 bg-white px-4 pt-safe md:hidden">
          <Link to="/admin" className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
            <Shield className="h-4 w-4 text-indigo-600" /> Admin
          </Link>
          <Link to="/dashboard" className="text-xs font-medium text-slate-500 hover:text-slate-900">
            Exit ↗
          </Link>
        </header>

        {/* Top right user pill on desktop */}
        <header className="hidden h-16 items-center justify-between border-b border-slate-200 bg-white px-6 md:flex">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-600">Admin</p>
            <p className="text-sm text-slate-500">Authenticated as <span className="font-medium text-slate-900">{user?.email}</span></p>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-all hover:border-slate-300 hover:shadow-sm active:scale-[0.98]"
          >
            <LogOut className="h-3.5 w-3.5" /> Log out
          </button>
        </header>

        {/* Mobile nav row (flat — section labels would wrap too much) */}
        <div className="overflow-x-auto border-b border-slate-200 bg-white md:hidden">
          <div className="flex gap-1 px-2 py-1.5">
            {NAV_FLAT.map(({ to, end, label, Icon, tid }) => (
              <NavLink
                key={`m-${to}`}
                to={to}
                end={end}
                data-testid={`m-${tid}`}
                className={({ isActive }) =>
                  `flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                    isActive ? "bg-indigo-600 text-white" : "text-slate-500 hover:bg-slate-100"
                  }`
                }
              >
                <Icon className="h-3 w-3" />
                {label}
              </NavLink>
            ))}
          </div>
        </div>

        <ImpersonationBanner user={user} onStop={onStopImpersonating} />

        <main className="flex-1 p-6 sm:p-8" data-testid="admin-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
